import { produce } from 'immer';
import {
  OmniStyleElement,
  OmniIconElement,
  OmniTooltipElement,
  OmniElement,
  styleMap,
  classMap,
  html,
  css,
} from 'omni-ui';

import { pixToRem } from '../helpers/utils.js';

import './campaign-draggable-chips.js';
import './campaign-input-card.js';

import { AUDIENCE_INSIGHTS_DEFAULT } from '../helpers/constants.js';

OmniStyleElement.register();
OmniIconElement.register();
OmniTooltipElement.register();

/**
 * Class for Campaign Audience Insights Module
 * @extends OmniElement
 */
export class CampaignAudienceInsights extends OmniElement {
  /**
   * Attach Objects Lit Properties
   * @property {string} env - Application environment
   * @property {Object} context - Values passed in from wrapper/router
   * @property {Object} context.clientId - The client ID to be passed to the API so objects are scoped to the client
   * @property {Object} context.uuid_module - This module's UUID
   * @property {string} token - API JWT token
   * @property {string} label - Label text for control
   * @property {string} moduleTitle - Title text for module block
   * @property {Function} save - The save function from Campaign Step Detail
   * @property {Object[]} savedData - Data already saved in the API
   * @property {string} savedData[].uuid - The ID, used to filter selected objects on load
   * @property {Object} areas - A state value that saves the area chips of the visualization
   * @property {Object[]} areas.thinks - An array of chip objects for the think/feel area
   * @property {Object[]} areas.hears - An array of chip objects for the hears area
   * @property {Object[]} areas.sees - An array of chip objects for the sees area
   * @property {Object[]} areas.says - An array of chip objects for the says area
   * @property {Object[]} areas.does - An array of chip objects for the does area
   */
  static get properties() {
    return {
      env: { type: String },
      context: { type: Object },
      token: { type: String },
      moduleTitle: { type: String },
      save: { attribute: false },
      savedData: { type: Object },
      areas: { type: Object },
      tempAddInputValue: { type: String },
      backgroundColors: { type: Object },
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .omni .box {
          padding: 2.143rem 2.86rem 4.29rem;
        }

        .viz-grid {
          height: ${pixToRem(890)}; /* from zeplin */
          max-height: 80vh;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-template-rows: 3fr 2fr 2fr 3fr;
          grid-template-areas:
            'thinks thinks'
            'hears    sees'
            'hears    says'
            'does     does';
          gap: 2px;
          z-index: 4;
        }

        .viz-grid > div {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .viz-controls {
          position: absolute;
          display: flex;
          align-items: center;
        }

        .viz-label {
          letter-spacing: 0.02rem;
          color: rgb(var(--rgb-core-dark));
          margin-right: 1.1428rem;
        }

        .viz-thinks {
          grid-area: thinks;
        }
        .viz-thinks .viz-controls {
          top: 1.785rem;
          left: 50%;
          transform: translateX(-50%);
          z-index: 4;
        }
        .viz-hears {
          grid-area: hears;
        }
        .viz-hears .viz-controls {
          top: 1.785rem;
          left: 2.142rem;
          z-index: 3;
        }
        .viz-sees {
          grid-area: sees;
        }
        .viz-says {
          grid-area: says;
        }
        .viz-sees .viz-controls {
          z-index: 3;
        }
        .viz-says .viz-controls {
          z-index: 2;
        }
        .viz-sees .viz-controls,
        .viz-says .viz-controls {
          top: 1.785rem;
          right: 2.142rem;
        }
        .viz-does {
          grid-area: does;
        }
        .viz-does .viz-controls {
          top: 1.785rem;
          left: 2.142rem;
          z-index: 2;
        }

        /* z-index tomfoolery */
        .viz-does campaign-draggable-chips::part(chip),
        .viz-says campaign-draggable-chips::part(chip) {
          z-index: 1;
        }

        omni-tooltip {
          z-index: 4; /* higher to avoid chip/area conflicts */
          margin-left: 0.5rem; /* always appears to right of label */
        }

        .textarea-field-row {
          display: flex;
          justify-content: space-between;
          margin: 2.142rem 0 0 0;
        }
        .textarea-label {
          font-size: 0.785rem;
          margin-bottom: 1.07rem;
          color: rgb(var(--rgb-core-dark));
          letter-spacing: 0.02rem;
        }
        .textarea-field {
          flex-basis: calc((100% / 3) - 1.07rem);
          flex-grow: 0;
          flex-shrink: 0;
        }

        textarea.textarea:read-only,
        textarea.textarea:read-only:is(:focus, :active, :hover) {
          resize: none;
          background-color: transparent;
          border: none;
          box-shadow: none;
          color: rgb(var(--rgb-core-dark));
        }

        /**
         * TODO: revisit this z-index battle and maybe have input-card append to the body
         */
        campaign-input-card::part(toggle-open-button) {
          z-index: 3;
        }
        campaign-input-card::part(module-container) {
          z-index: 4;
        }

        campaign-input-card::part(input-container) {
          margin-bottom: 1rem;
        }

        campaign-input-card::part(input-length-indicator) {
          margin-top: 0.285rem;
          font-size: 0.8rem;
          color: rgb(var(--rgb-core-medium));
        }
        campaign-input-card::part(input-length-max) {
          color: rgb(var(--rgb-core-dark));
        }

        campaign-draggable-chips::part(chip-dragging) {
          z-index: 4;
        }
      `,
    ];
  }

  constructor() {
    super();

    this.areas = {
      thinks: [],
      hears: [],
      sees: [],
      says: [],
      does: [],
    };
    this.schema = {};
    this.moduleTitle = '';
    this.backgroundColors = {
      thinks: 'rgba(var(--rgb-primary), 0.1)',
      hears: 'rgba(var(--rgb-primary), 0.4)',
      sees: 'rgba(var(--rgb-primary), 0.2)',
      says: 'rgba(var(--rgb-primary), 0.2)',
      does: 'rgba(var(--rgb-primary), 0.1)',
    };
    this.tempAddInputValue = '';
    this.outputValue = '';
    this.painsValue = '';
    this.gainsValue = '';
  }

  connectedCallback() {
    super.connectedCallback();
    this.schema ||= AUDIENCE_INSIGHTS_DEFAULT;
    this.moduleTitle ||= AUDIENCE_INSIGHTS_DEFAULT.title;
    this.addEventListener(
      'campaign-input-card-close',
      this._resetTempAddInputValue,
    );
  }

  disconnectedCallback() {
    this.removeEventListener(
      'campaign-input-card-close',
      this._resetTempAddInputValue,
    );
    super.disconnectedCallback();
  }

  firstUpdated() {
    if (Object.keys(this.savedData || {})?.length) {
      this.areas = this.savedData.areas;
      this.outputValue = this.savedData.output;
      this.painsValue = this.savedData.pains;
      this.gainsValue = this.savedData.gains;
    }
  }

  _resetTempAddInputValue() {
    this.tempAddInputValue = '';
  }

  _handleChange() {
    const { uuid_module: moduleId } = this.context;
    this.dispatchNewEvent('omni-module-changed', {
      detail: {
        moduleId,
        moduleType: 'audience insight module',
        saveAction: () =>
          this.save({
            output: this.outputValue,
            pains: this.painsValue,
            gains: this.gainsValue,
            areas: this.areas,
          }),
      },
    });
  }

  _addItem(key, text) {
    this.areas = produce(this.areas, draft => {
      draft[key].push({ text, top: '50%', left: '15%' });
    });
    this._resetTempAddInputValue();
    this._handleChange();
  }

  _removeItem(key, ind) {
    this.areas = produce(this.areas, draft => {
      draft[key].splice(ind, 1);
    });
    this._handleChange();
  }

  _updateItem(key, ind, { top, left }) {
    this.areas = produce(this.areas, draft => {
      draft[key][ind].top = top;
      draft[key][ind].left = left;
    });
    this._handleChange();
  }

  _renderTooltip(metaTypeKey, metaItemKey) {
    return html`
      <omni-tooltip
        place=${this.schema[metaTypeKey][metaItemKey].tooltipPosition}
      >
        <omni-icon
          slot="invoker"
          class="is-size-2"
          icon-id="omni:informative:info"
        ></omni-icon>
        <div slot="content">
          ${this.schema[metaTypeKey][metaItemKey].tooltip}
        </div>
      </omni-tooltip>
    `;
  }

  _renderTextAreas() {
    const {
      context: { readonly },
      schema,
    } = this;

    return html`
      <div class=${classMap({ 'textarea-field-row': true, readonly })}>
        ${Object.keys(schema.fields).map(
          k => html`
            <div class="textarea-field">
              <div class="textarea-label">
                ${schema.fields[k].label} ${this._renderTooltip('fields', k)}
              </div>
              <textarea
                class="textarea has-background-pale-grey"
                .value=${this[`${k}Value`]}
                @input=${({ target }) => {
                  this[`${k}Value`] = target.value;
                  this._handleChange();
                }}
                ?readonly=${readonly}
              ></textarea>
            </div>
          `,
        )}
      </div>
    `;
  }

  _renderItems(key) {
    return html`
      <campaign-draggable-chips
        .chips=${this.areas[key]}
        .handleRemoveChip=${ind => {
          this._removeItem(key, ind);
        }}
        .handleUpdateChip=${(ind, val) => {
          this._updateItem(key, ind, val);
        }}
        .readonly=${this.context.readonly}
      ></campaign-draggable-chips>
    `;
  }

  _renderInputCard(key) {
    const {
      context: { readonly },
      schema,
    } = this;
    if (readonly) return null;

    return html`
      <campaign-input-card
        headerText=${schema.areas[key].label}
        tooltipText="Add Content"
        confirmBtnText="Add"
        alignCard=${schema.areas[key].popoverPosition}
        .handleSubmit=${() => {
          this._addItem(key, this.tempAddInputValue);
        }}
        .bodyTemplate=${() => html`
          <div part="input-container">
            <input
              required
              class="input input-card-input"
              maxlength="40"
              placeholder="Enter keyword or phrase"
              .value=${this.tempAddInputValue}
              @input=${e => {
                this.tempAddInputValue = e.target.value;
              }}
            />
            <div
              part=${`input-length-indicator${
                this.tempAddInputValue.length === 40 ? ' input-length-max' : ''
              }`}
            >
              ${this.tempAddInputValue.length}/40
            </div>
          </div>
        `}
        @campaign-input-card-open=${({ target }) => {
          target.shadowRoot.querySelector('.input-card-input')?.focus();
        }}
      ></campaign-input-card>
    `;
  }

  render() {
    return html`
      <omni-style>
        <div class="box">
          <h2 class="title mb-5 is-3">${this.moduleTitle}</h2>
          <div class="viz-grid">
            ${Object.keys(this.areas).map(
              k => html`
                <div
                  class="area viz-${k}"
                  style=${styleMap({
                    backgroundColor: this.backgroundColors?.[k],
                  })}
                >
                  <div class="viz-controls">
                    <div class="viz-label">
                      ${this.schema.areas[k].label}
                      ${this._renderTooltip('areas', k)}
                    </div>
                    ${this._renderInputCard(k)}
                  </div>
                  ${this._renderItems(k)}
                </div>
                <!-- .area -->
              `,
            )}
          </div>
          <!-- .viz-grid -->
          ${this._renderTextAreas()}
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-audience-insights', CampaignAudienceInsights);
